import { useState } from 'react';

import type { ChannelDto } from '../../api/types';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Card, CardBody, CardHeader } from '../ui/card';
import { Select } from '../ui/select';

export interface PersonalChannelsCardProps {
  personal: ChannelDto[];
  groups: ChannelDto[];
  /** owner/lead only — managers see the list read-only. */
  canConfirm: boolean;
  busyId: string | null;
  error: string | null;
  onConfirm: (id: string, linkedGroupChannelId: string | null) => void;
}

function statusBadge(channel: ChannelDto) {
  if (channel.confirmedAt) return <Badge tone="success">подтверждён</Badge>;
  return <Badge tone="warning">ожидает подтверждения</Badge>;
}

/**
 * Personal (`vk_personal`) channels with their confirmation state and the
 * community each one is linked to — the inbox, funnel and analytics roll the
 * personal channel up into that community (§4.3).
 */
export function PersonalChannelsCard(props: PersonalChannelsCardProps) {
  const { personal, groups, canConfirm, busyId, error, onConfirm } = props;
  const [linkChoice, setLinkChoice] = useState<Record<string, string>>({});

  return (
    <Card>
      <CardHeader className="font-medium">Личные каналы</CardHeader>
      <CardBody className="space-y-3">
        {personal.length === 0 && (
          <p className="text-sm text-muted-fg">
            Личных каналов пока нет. Менеджер привязывает свой аккаунт из панели расширения, после чего канал
            появится здесь и его нужно будет подтвердить.
          </p>
        )}
        {personal.map((channel) => {
          const linked = groups.find((g) => g.id === channel.linkedGroupChannelId) ?? null;
          const selected = linkChoice[channel.id] ?? channel.linkedGroupChannelId ?? '';
          return (
            <div key={channel.id} className="space-y-2 rounded border border-border px-3 py-2">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-medium">{channel.title}</span>
                {channel.vkUserId !== null && <span className="text-sm text-muted-fg">id{channel.vkUserId}</span>}
                {statusBadge(channel)}
                {linked ? (
                  <Badge tone="info">группа: {linked.title}</Badge>
                ) : (
                  <Badge tone="neutral">группа не выбрана</Badge>
                )}
              </div>
              {canConfirm && (
                <div className="flex flex-wrap items-center gap-2">
                  <Select
                    className="w-64"
                    value={selected}
                    onChange={(e) => setLinkChoice((prev) => ({ ...prev, [channel.id]: e.target.value }))}
                  >
                    <option value="">Без привязки к сообществу</option>
                    {groups.map((g) => (
                      <option key={g.id} value={g.id}>
                        {g.title}
                      </option>
                    ))}
                  </Select>
                  <Button
                    size="sm"
                    disabled={busyId === channel.id}
                    onClick={() => onConfirm(channel.id, selected === '' ? null : selected)}
                  >
                    {busyId === channel.id
                      ? 'Сохраняем…'
                      : channel.confirmedAt
                        ? 'Обновить привязку'
                        : 'Подтвердить канал'}
                  </Button>
                </div>
              )}
            </div>
          );
        })}
        {error && <p className="text-sm text-red-600">{error}</p>}
      </CardBody>
    </Card>
  );
}
